import { Fragment, useEffect, useMemo, useState } from "react";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Search, Pencil, Trash2, AlertTriangle, Image as ImageIcon, ChevronDown, ChevronRight, Download, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import FileUploader from "@/components/FileUploader";

const TYPES = [
  { value: "accident",  label: "Accident" },
  { value: "damage",    label: "Damage" },
  { value: "theft",     label: "Theft" },
  { value: "breakdown", label: "Breakdown" },
  { value: "fine",      label: "Fine / Penalty" },
  { value: "near_miss", label: "Near miss" },
  { value: "other",     label: "Other" },
];
const TYPE_LABEL = Object.fromEntries(TYPES.map(t => [t.value, t.label]));

const SEVERITIES = [
  { value: "minor",     label: "Minor" },
  { value: "moderate",  label: "Moderate" },
  { value: "major",     label: "Major" },
  { value: "write_off", label: "Write-off" },
];
const SEV_LABEL = Object.fromEntries(SEVERITIES.map(s => [s.value, s.label]));
const SEV_TONE = {
  minor: "bg-slate-100 text-slate-700 border-slate-200",
  moderate: "bg-amber-50 text-amber-800 border-amber-200",
  major: "bg-orange-50 text-orange-800 border-orange-200",
  write_off: "bg-red-50 text-red-700 border-red-200",
};

const CLAIM_STATUSES = [
  { value: "not_reported", label: "Not reported" },
  { value: "reported",     label: "Reported" },
  { value: "in_progress",  label: "In progress" },
  { value: "settled",      label: "Settled" },
  { value: "closed",       label: "Closed" },
];
const CSTATUS_LABEL = Object.fromEntries(CLAIM_STATUSES.map(s => [s.value, s.label]));

const I_EMPTY = {
  vehicle_id: "", date: new Date().toISOString().slice(0, 10), time: "",
  type: "accident", severity: "minor", location: "", description: "",
  driver_name: "", third_party_involved: false, third_party_details: "",
  police_reported: false, police_ref: "",
  insurance_claim_ref: "", claim_status: "not_reported",
  cost: "", repair_supplier_id: "", repair_supplier_snapshot: {},
  repair_completed: false, repair_date: "",
  fine_amount: "", penalty_ref: "", license_points: "", fine_due_date: "",
  stolen_items: "", recovered: false,
  notes: "",
};

// Field-visibility map: which sections show for each Type
// Keys: party3 | police | insurance | repair | severity | cost | fine | theft
const TYPE_FIELDS = {
  accident:  { party3: true,  police: true,  insurance: true,  repair: true,  severity: true,  cost: true,  fine: false, theft: false },
  damage:    { party3: true,  police: true,  insurance: true,  repair: true,  severity: true,  cost: true,  fine: false, theft: false },
  theft:     { party3: false, police: true,  insurance: true,  repair: false, severity: true,  cost: true,  fine: false, theft: true  },
  breakdown: { party3: false, police: false, insurance: false, repair: true,  severity: false, cost: true,  fine: false, theft: false },
  fine:      { party3: false, police: false, insurance: false, repair: false, severity: false, cost: false, fine: true,  theft: false },
  near_miss: { party3: false, police: false, insurance: false, repair: false, severity: false, cost: false, fine: false, theft: false },
  other:     { party3: true,  police: true,  insurance: true,  repair: true,  severity: true,  cost: true,  fine: false, theft: false },
};

export default function Incidents() {
  const [records, setRecords] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [filterVehicle, setFilterVehicle] = useState("__all");
  const [filterStatus, setFilterStatus] = useState("__all");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [form, setForm] = useState(I_EMPTY);
  const [saving, setSaving] = useState(false);

  const vehById = useMemo(() => Object.fromEntries(vehicles.map(v => [v.id, v])), [vehicles]);
  const supById = useMemo(() => Object.fromEntries(suppliers.map(s => [s.id, s])), [suppliers]);

  const load = () => Promise.all([
    api.get("/incidents").then(r => setRecords(r.data || [])),
    api.get("/vehicles").then(r => setVehicles(r.data || [])),
    api.get("/maintenance-suppliers").then(r => setSuppliers(r.data || [])),
  ]).catch(e => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = records;
    if (filterVehicle !== "__all") list = list.filter(r => r.vehicle_id === filterVehicle);
    if (filterStatus !== "__all") list = list.filter(r => r.claim_status === filterStatus);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(r => {
        const v = vehById[r.vehicle_id];
        return [r.description, r.location, r.driver_name, r.third_party_details, r.police_ref, r.insurance_claim_ref, r.notes,
                TYPE_LABEL[r.type], SEV_LABEL[r.severity], v?.registration, v?.make, v?.model]
          .filter(Boolean).some(s => s.toLowerCase().includes(q));
      });
    }
    return list;
  }, [records, filterVehicle, filterStatus, search, vehById]);

  const totalCost = useMemo(() => filtered.reduce((s, r) => s + (Number(r.cost) || 0), 0), [filtered]);

  const startNew = () => {
    const def = (filterVehicle !== "__all" && filterVehicle) || "";
    setEditing(null); setForm({ ...I_EMPTY, vehicle_id: def }); setOpen(true);
  };
  const startEdit = (r) => {
    setEditing(r);
    setForm({ ...I_EMPTY, ...r, cost: r.cost ?? "", repair_supplier_snapshot: r.repair_supplier_snapshot || {} });
    setOpen(true);
  };
  const onClose = () => { setOpen(false); setEditing(null); setForm(I_EMPTY); };

  const save = async () => {
    if (!form.vehicle_id) { toast.error("Pick a vehicle"); return; }
    if (!form.date) { toast.error("Date is required"); return; }
    const payload = {
      ...form,
      cost: form.cost === "" || form.cost === null ? 0 : Number(form.cost),
      fine_amount: form.fine_amount === "" || form.fine_amount === null ? null : Number(form.fine_amount),
      license_points: form.license_points === "" || form.license_points === null ? null : Number(form.license_points),
    };
    setSaving(true);
    try {
      if (editing) await api.put(`/incidents/${editing.id}`, payload);
      else         await api.post("/incidents", payload);
      toast.success(editing ? "Incident updated" : "Incident logged");
      onClose(); await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm("Delete this incident?")) return;
    try { await api.delete(`/incidents/${r.id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const toggle = (id) => setExpandedId(curr => curr === id ? null : id);

  const registerQuery = useMemo(() => {
    const p = new URLSearchParams();
    if (filterVehicle !== "__all") p.set("vehicle_id", filterVehicle);
    const s = p.toString();
    return s ? `?${s}` : "";
  }, [filterVehicle]);

  return (
    <div data-testid="incidents-page" className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Fleet · Incidents</div>
          <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900 mt-2">Incidents & accidents</h1>
          <p className="text-sm text-slate-600 mt-1">Log accidents, damage, theft, breakdowns and fines against each vehicle. Track insurance claims and download an incident report PDF for your insurer.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" data-testid="inc-register-pdf" className="rounded-sm border-slate-300">
            <a href={fileUrl(`/incidents/export.pdf${registerQuery}`)} download rel="noopener">
              <Download className="w-4 h-4 mr-1.5" /> Register PDF
            </a>
          </Button>
          <Button asChild variant="outline" data-testid="inc-register-xlsx" className="rounded-sm border-slate-300">
            <a href={fileUrl(`/incidents/export.xlsx${registerQuery}`)} download rel="noopener">
              <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Register Excel
            </a>
          </Button>
          <Button data-testid="inc-add-btn" onClick={startNew} disabled={vehicles.length === 0} className="rounded-sm bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300">
            <Plus className="w-4 h-4 mr-1.5" /> Log incident
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="inc-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search description, location, driver, claim ref…" className="pl-9 rounded-sm border-slate-300" />
        </div>
        <div className="min-w-[220px]">
          <Select value={filterVehicle} onValueChange={setFilterVehicle}>
            <SelectTrigger data-testid="inc-filter-vehicle" className="rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All vehicles</SelectItem>
              {vehicles.map(v => (
                <SelectItem key={v.id} value={v.id}>{v.registration} · {[v.make, v.model].filter(Boolean).join(" ") || "—"}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[180px]">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="inc-filter-status" className="rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All claim statuses</SelectItem>
              {CLAIM_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="text-xs text-slate-500 tabular-nums ml-auto">
          {filtered.length} incident{filtered.length === 1 ? "" : "s"} · total £{totalCost.toFixed(2)}
        </div>
      </div>

      <Card className="rounded-sm border border-slate-200 shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="incidents-table">
            <thead className="bg-slate-900 text-white">
              <tr>
                <Th className="w-8"></Th>
                <Th>Date</Th>
                <Th>Vehicle</Th>
                <Th>Type</Th>
                <Th>Severity</Th>
                <Th>Driver</Th>
                <Th>Description</Th>
                <Th>Claim status</Th>
                <Th>Cost</Th>
                <Th className="text-right pr-4">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={10} className="text-center py-12 text-slate-500 text-sm">
                    <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    {records.length === 0 ? "No incidents logged yet." : "No incidents match your filters."}
                  </td>
                </tr>
              )}
              {filtered.map(r => {
                const v = vehById[r.vehicle_id];
                const isOpen = expandedId === r.id;
                return (
                  <Fragment key={r.id}>
                    <tr className="border-t border-slate-200 hover:bg-slate-50">
                      <Td className="pr-0">
                        <button data-testid={`inc-expand-${r.id}`} onClick={() => toggle(r.id)} className="h-7 w-7 inline-flex items-center justify-center rounded-sm hover:bg-slate-200 text-slate-600">
                          {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                      </Td>
                      <Td><div className="tabular-nums text-slate-900">{r.date || "—"}</div>{r.time && <div className="text-xs text-slate-500">{r.time}</div>}</Td>
                      <Td>
                        <div className="font-medium text-slate-900 tracking-wider">{v?.registration || "—"}</div>
                        <div className="text-xs text-slate-500">{[v?.make, v?.model].filter(Boolean).join(" ")}</div>
                      </Td>
                      <Td><Badge variant="outline" className="rounded-sm">{TYPE_LABEL[r.type] || r.type}</Badge></Td>
                      <Td>
                        <span className={`inline-block text-[10px] uppercase tracking-wider border px-1.5 py-0.5 rounded-sm ${SEV_TONE[r.severity] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
                          {SEV_LABEL[r.severity] || r.severity}
                        </span>
                      </Td>
                      <Td><div className="text-slate-900">{r.driver_name || "—"}</div></Td>
                      <Td><div className="text-slate-900 max-w-xs truncate" title={r.description || ""}>{r.description || "—"}</div></Td>
                      <Td><div className="text-xs text-slate-700">{CSTATUS_LABEL[r.claim_status] || "—"}</div>{r.insurance_claim_ref && <div className="text-xs text-slate-500 font-mono">{r.insurance_claim_ref}</div>}</Td>
                      <Td><div className="tabular-nums text-slate-900">£{Number(r.cost || 0).toFixed(2)}</div></Td>
                      <Td className="text-right pr-4">
                        <Button asChild size="sm" variant="ghost" data-testid={`inc-pdf-${r.id}`} className="rounded-sm h-8 w-8 p-0" title="Download incident report">
                          <a href={fileUrl(`/incidents/${r.id}/pdf`)} download rel="noopener">
                            <Download className="w-4 h-4" />
                          </a>
                        </Button>
                        <Button size="sm" variant="ghost" data-testid={`inc-photos-${r.id}`} onClick={() => toggle(r.id)} className="rounded-sm h-8 w-8 p-0" title="Photos & documents">
                          <ImageIcon className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" data-testid={`inc-edit-${r.id}`} onClick={() => startEdit(r)} className="rounded-sm h-8 w-8 p-0">
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" data-testid={`inc-delete-${r.id}`} onClick={() => remove(r)} className="rounded-sm h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </Td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-slate-50/70 border-t border-slate-200">
                        <td colSpan={10} className="px-6 py-5">
                          <FileUploader entityType="incident" entityId={r.id} label={`Photos & documents — ${v?.registration || ""} ${r.date}`} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent data-testid="incident-dialog" className="rounded-sm sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{editing ? "Edit incident" : "Log incident"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <Field label="Vehicle *">
              <Select value={form.vehicle_id} onValueChange={(v) => setForm({ ...form, vehicle_id: v })}>
                <SelectTrigger data-testid="inc-vehicle" className="rounded-sm border-slate-300"><SelectValue placeholder="Pick a vehicle" /></SelectTrigger>
                <SelectContent>
                  {vehicles.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.registration} · {[v.make, v.model].filter(Boolean).join(" ") || "—"}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Driver">
              <Input data-testid="inc-driver" value={form.driver_name} onChange={(e) => setForm({ ...form, driver_name: e.target.value })} placeholder="Driver name" className="rounded-sm border-slate-300" />
            </Field>
            <Field label="Date *">
              <Input data-testid="inc-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm border-slate-300" />
            </Field>
            <Field label="Time">
              <Input data-testid="inc-time" type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="rounded-sm border-slate-300" />
            </Field>
            <Field label="Type">
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="inc-type" className="rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            {(() => { const vis = TYPE_FIELDS[form.type] || TYPE_FIELDS.other; return (
            <>
            {vis.severity && (
            <Field label="Severity">
              <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                <SelectTrigger data-testid="inc-severity" className="rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent>{SEVERITIES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            )}
            <div className="sm:col-span-2">
              <Field label="Location">
                <Input data-testid="inc-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. A41 northbound, junction 14" className="rounded-sm border-slate-300" />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Description / what happened">
                <Textarea data-testid="inc-desc" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Describe what happened…" className="rounded-sm border-slate-300" />
              </Field>
            </div>

            {vis.party3 && (
            <Field label="Third party involved?">
              <div className="h-10 flex items-center gap-3">
                <Switch data-testid="inc-3p" checked={form.third_party_involved} onCheckedChange={(v) => setForm({ ...form, third_party_involved: v })} />
                <span className="text-sm text-slate-600">{form.third_party_involved ? "Yes" : "No"}</span>
              </div>
            </Field>
            )}
            {vis.police && (
            <Field label="Police reported?">
              <div className="h-10 flex items-center gap-3">
                <Switch data-testid="inc-police" checked={form.police_reported} onCheckedChange={(v) => setForm({ ...form, police_reported: v })} />
                <span className="text-sm text-slate-600">{form.police_reported ? "Yes" : "No"}</span>
              </div>
            </Field>
            )}
            {vis.party3 && form.third_party_involved && (
              <div className="sm:col-span-2">
                <Field label="Third party details">
                  <Textarea data-testid="inc-3p-details" rows={2} value={form.third_party_details} onChange={(e) => setForm({ ...form, third_party_details: e.target.value })} placeholder="Name, vehicle reg, contact, insurer…" className="rounded-sm border-slate-300" />
                </Field>
              </div>
            )}
            {vis.police && form.police_reported && (
              <Field label="Police ref.">
                <Input data-testid="inc-police-ref" value={form.police_ref} onChange={(e) => setForm({ ...form, police_ref: e.target.value })} placeholder="Crime / incident ref." className="rounded-sm border-slate-300" />
              </Field>
            )}

            {vis.theft && (<>
              <div className="sm:col-span-2">
                <Field label="Stolen items / contents">
                  <Textarea data-testid="inc-stolen" rows={2} value={form.stolen_items} onChange={(e) => setForm({ ...form, stolen_items: e.target.value })} placeholder="What was taken? Tools, parcels, the whole vehicle…" className="rounded-sm border-slate-300" />
                </Field>
              </div>
              <Field label="Recovered?">
                <div className="h-10 flex items-center gap-3">
                  <Switch data-testid="inc-recovered" checked={form.recovered} onCheckedChange={(v) => setForm({ ...form, recovered: v })} />
                  <span className="text-sm text-slate-600">{form.recovered ? "Yes" : "No"}</span>
                </div>
              </Field>
            </>)}

            {vis.fine && (<>
              <Field label="Fine amount (£)">
                <Input data-testid="inc-fine-amount" type="number" step="0.01" value={form.fine_amount} onChange={(e) => setForm({ ...form, fine_amount: e.target.value })} className="rounded-sm border-slate-300" />
              </Field>
              <Field label="Penalty / PCN ref.">
                <Input data-testid="inc-penalty-ref" value={form.penalty_ref} onChange={(e) => setForm({ ...form, penalty_ref: e.target.value })} className="rounded-sm border-slate-300" />
              </Field>
              <Field label="Licence points">
                <Input data-testid="inc-points" type="number" inputMode="numeric" value={form.license_points} onChange={(e) => setForm({ ...form, license_points: e.target.value })} placeholder="0" className="rounded-sm border-slate-300" />
              </Field>
              <Field label="Fine due date">
                <Input data-testid="inc-fine-due" type="date" value={form.fine_due_date} onChange={(e) => setForm({ ...form, fine_due_date: e.target.value })} className="rounded-sm border-slate-300" />
              </Field>
            </>)}

            {vis.insurance && (<>
            <Field label="Insurance claim ref.">
              <Input data-testid="inc-claim-ref" value={form.insurance_claim_ref} onChange={(e) => setForm({ ...form, insurance_claim_ref: e.target.value })} className="rounded-sm border-slate-300" />
            </Field>
            <Field label="Claim status">
              <Select value={form.claim_status} onValueChange={(v) => setForm({ ...form, claim_status: v })}>
                <SelectTrigger data-testid="inc-claim-status" className="rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent>{CLAIM_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            </>)}
            {vis.cost && (
            <Field label="Cost / excess (£)">
              <Input data-testid="inc-cost" type="number" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} className="rounded-sm border-slate-300" />
            </Field>
            )}
            {vis.repair && (<>
            <Field label="Repair completed?">
              <div className="h-10 flex items-center gap-3">
                <Switch data-testid="inc-repaired" checked={form.repair_completed} onCheckedChange={(v) => setForm({ ...form, repair_completed: v })} />
                <span className="text-sm text-slate-600">{form.repair_completed ? "Done" : "Pending"}</span>
              </div>
            </Field>
            <div className="sm:col-span-2">
              <Field label={form.type === "breakdown" ? "Recovered by (supplier)" : "Repaired by (maintenance supplier)"}>
                <Select
                  value={form.repair_supplier_id || "__none"}
                  onValueChange={(v) => {
                    if (v === "__none") setForm({ ...form, repair_supplier_id: "", repair_supplier_snapshot: {} });
                    else {
                      const s = supById[v];
                      setForm({ ...form, repair_supplier_id: v, repair_supplier_snapshot: s || {} });
                    }
                  }}
                >
                  <SelectTrigger data-testid="inc-repair-sup" className="rounded-sm border-slate-300"><SelectValue placeholder="(none)" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="__none">— Not assigned —</SelectItem>
                    {suppliers.filter(s => s.is_active !== false).map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}{s.postcode ? ` · ${s.postcode}` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label={form.type === "breakdown" ? "Recovery date" : "Repair date"}>
              <Input data-testid="inc-repair-date" type="date" value={form.repair_date} onChange={(e) => setForm({ ...form, repair_date: e.target.value })} className="rounded-sm border-slate-300" />
            </Field>
            </>)}
            </>
            ); })()}
            <div className="sm:col-span-2">
              <Field label="Notes">
                <Textarea data-testid="inc-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm border-slate-300" />
              </Field>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
            <Button data-testid="inc-save" onClick={save} disabled={saving} className="rounded-sm bg-slate-900 hover:bg-slate-800">
              {saving ? "Saving…" : (editing ? "Save changes" : "Log incident")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Th({ children, className = "" }) { return <th className={`text-left text-[11px] uppercase tracking-wider font-semibold px-3 py-2.5 ${className}`}>{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-3 py-3 align-top ${className}`}>{children}</td>; }
function Field({ label, children }) {
  return (<div><Label className="text-xs text-slate-600">{label}</Label><div className="mt-1">{children}</div></div>);
}
